import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  SHIRT_COLORS,
  type ColorAllowance,
  type ShirtColor,
  type ShirtCut,
  type Size,
  type ShippingMethod,
  type ShippingMode,
} from '../types';
import { listProducts, type Product, type ProductSize } from '../api/products';
import { getShopStatus, type ShopStatus } from '../api/shopStatus';
import type { SelectedPaczkomat } from '../util/paczkomat';

export type ShopSelection = {
  color: ShirtColor;
  cut: ShirtCut;
  size: Size;
  qty: number;
};

export type CartItem = {
  id: string;
  productId: string;
  color: ShirtColor;
  cut: ShirtCut;
  size: Size;
  qty: number;
  unitPriceGrosze: number;
};

export type Verification = {
  firstName: string;
  externalOrderId: string;
  /** Across colours, already capped by each colour's share. */
  allowance: number;
  alreadyOrdered: number;
  remaining: number;
  /** Ticket count x per-colour quota for each colour, as the API computed it. */
  byColor: Record<ShirtColor, ColorAllowance>;
  /**
   * `ride_along` when an earlier order for this external order still has an open parcel: the
   * delivery step collapses and shipping costs nothing. Like `remaining`, a snapshot the server
   * re-decides at creation - POST /orders answers `shipping_plan_changed` if it moved.
   */
  shippingMode: ShippingMode;
};

export type CheckoutForm = {
  lastName: string;
  email: string;
  emailValid: boolean;
  // E.164 e.g. "+48512345678" — empty string until the user types a complete number.
  phone: string;
  phoneValid: boolean;
  paczkomat?: SelectedPaczkomat;
  courier?: { street: string; postalCode: string; city: string };
  /** ISO 3166-1 alpha-2 destination, required by `zagranica` and ignored by every other method. */
  country: string;
  /** Free-text note that travels with the order; the international step is what asks for one. */
  comment: string;
  method: ShippingMethod;
};

export type LastOrder = {
  id: string;
  token: string;
};

export type CatalogState =
  | { status: 'loading' }
  | { status: 'ready'; products: Product[]; shopStatus: ShopStatus }
  | { status: 'error'; error: string };

export type ShopState = {
  verification: Verification | null;
  setVerification: (v: Verification | null) => void;
  selection: ShopSelection;
  setSelection: (next: Partial<ShopSelection>) => void;
  cart: CartItem[];
  addToCart: (item: Omit<CartItem, 'id'>) => void;
  removeFromCart: (id: string) => void;
  cartTotalQty: number;
  cartTotalGrosze: number;
  checkout: CheckoutForm;
  setCheckout: (next: Partial<CheckoutForm>) => void;
  lastOrder: LastOrder | null;
  setLastOrder: (order: LastOrder | null) => void;
  resetOrderSession: () => void;
  catalog: CatalogState;
  refreshCatalog: () => Promise<void>;
  /**
   * For a `shop_closed` answer from POST /orders. Refetching would not do: /shop-status is cached
   * for up to a minute and can still say open.
   */
  markShopClosed: () => void;
};

const INITIAL_SELECTION: ShopSelection = {
  color: 'czarna',
  cut: 'męski',
  size: 'M',
  qty: 1,
};

const INITIAL_CHECKOUT: CheckoutForm = {
  lastName: '',
  email: '',
  emailValid: false,
  phone: '',
  phoneValid: false,
  country: '',
  comment: '',
  method: 'paczkomat',
  paczkomat: undefined,
};

const ShopCtx = createContext<ShopState | null>(null);

const VERIFICATION_STORAGE_KEY = 'teneryfa.shop.verification.v2';

// The gate is re-enforced by POST /orders on every submit, so keeping the passed gate in
// sessionStorage costs nothing in trust and saves the holder from re-typing the order id after
// a refresh or a return trip from Stripe.
//
// The remaining count is a snapshot, not an authority: another order against the same external
// id can land while this one is being filled, and the transaction in POST /orders is what
// actually decides. Storing it only keeps the cart from offering shirts that are already gone.
function readStoredVerification(): Verification | null {
  try {
    const raw = sessionStorage.getItem(VERIFICATION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Verification> | null;
    const firstName = parsed?.firstName?.trim();
    const externalOrderId = parsed?.externalOrderId?.trim();
    if (!firstName || !externalOrderId) return null;
    if (
      typeof parsed?.allowance !== 'number' ||
      typeof parsed?.alreadyOrdered !== 'number' ||
      typeof parsed?.remaining !== 'number' ||
      !isAllowanceByColor(parsed?.byColor)
    ) {
      return null;
    }
    // A session stored before the ride-along shipping step existed has no mode. Treating it as
    // 'choose' only means the buyer is offered a delivery step; POST /orders overrides it.
    const shippingMode: ShippingMode =
      parsed?.shippingMode === 'ride_along' || parsed?.shippingMode === 'closed'
        ? parsed.shippingMode
        : 'choose';
    return {
      firstName,
      externalOrderId,
      allowance: parsed.allowance,
      alreadyOrdered: parsed.alreadyOrdered,
      remaining: parsed.remaining,
      byColor: parsed.byColor,
      shippingMode,
    };
  } catch {
    // Private-mode Safari throws on sessionStorage access; treat that as "not verified".
    return null;
  }
}

// A session stored before the per-colour allowance existed has only colour-blind totals, which
// cannot be split after the fact; that holder passes the gate again.
function isAllowanceByColor(value: unknown): value is Record<ShirtColor, ColorAllowance> {
  if (!value || typeof value !== 'object') return false;
  return SHIRT_COLORS.every((c) => {
    const share = (value as Partial<Record<ShirtColor, Partial<ColorAllowance>>>)[c];
    return (
      typeof share?.allowance === 'number' &&
      typeof share.alreadyOrdered === 'number' &&
      typeof share.remaining === 'number'
    );
  });
}

function storeVerification(v: Verification | null): void {
  try {
    if (v) sessionStorage.setItem(VERIFICATION_STORAGE_KEY, JSON.stringify(v));
    else sessionStorage.removeItem(VERIFICATION_STORAGE_KEY);
  } catch {
    // See readStoredVerification.
  }
}

export function ShopProvider({ children }: { children: ReactNode }) {
  const [verification, setVerificationState] = useState<Verification | null>(readStoredVerification);
  const setVerification = useCallback((v: Verification | null) => {
    setVerificationState(v);
    storeVerification(v);
  }, []);
  const [selection, setSelectionState] = useState<ShopSelection>(INITIAL_SELECTION);
  const [checkout, setCheckoutState] = useState<CheckoutForm>(INITIAL_CHECKOUT);
  const [lastOrder, setLastOrder] = useState<LastOrder | null>(null);
  const [catalog, setCatalog] = useState<CatalogState>({ status: 'loading' });
  const [cart, setCart] = useState<CartItem[]>([]);

  const addToCart = useCallback((item: Omit<CartItem, 'id'>) => {
    setCart((prev) => {
      const idx = prev.findIndex(
        (r) => r.productId === item.productId && r.cut === item.cut && r.size === item.size,
      );
      if (idx >= 0) {
        const next = prev.slice();
        next[idx] = { ...next[idx]!, qty: next[idx]!.qty + item.qty };
        return next;
      }
      return [...prev, { ...item, id: newCartItemId() }];
    });
  }, []);
  const removeFromCart = useCallback((id: string) => {
    setCart((prev) => prev.filter((r) => r.id !== id));
  }, []);

  // A placed order ends the session. Whoever uses this browser next - the same holder ordering
  // for a friend, or a phone passed around at a meetup - starts at the gate instead of
  // inheriting the previous holder's order id, address and contact details.
  const resetOrderSession = useCallback(() => {
    setCart([]);
    setCheckoutState(INITIAL_CHECKOUT);
    // The colour survives: refreshCatalog already narrowed it to a product that exists, and the
    // default colour may not be on sale.
    setSelectionState((prev) => ({ ...INITIAL_SELECTION, color: prev.color }));
    setLastOrder(null);
    setVerification(null);
  }, [setVerification]);

  const cartTotalQty = cart.reduce((s, r) => s + r.qty, 0);
  const cartTotalGrosze = cart.reduce((s, r) => s + r.qty * r.unitPriceGrosze, 0);

  const refreshCatalog = async () => {
    try {
      const [pr, ss] = await Promise.all([listProducts(), getShopStatus()]);
      const [first, ...rest] = pr.products;
      if (!first) {
        setCatalog({ status: 'error', error: 'no_active_product' });
        return;
      }
      const allProducts = [first, ...rest];
      setSelectionState((prev) =>
        allProducts.some((p) => p.color === prev.color)
          ? prev
          : { ...prev, color: first.color },
      );
      setCatalog({ status: 'ready', products: allProducts, shopStatus: ss });
    } catch (err) {
      console.error('[shop] catalog load failed', err);
      setCatalog({ status: 'error', error: err instanceof Error ? err.message : 'load_failed' });
    }
  };

  useEffect(() => {
    void refreshCatalog();
  }, []);

  const markShopClosed = useCallback(() => {
    setCatalog((prev) =>
      prev.status === 'ready' ? { ...prev, shopStatus: { ...prev.shopStatus, shop_open: false } } : prev,
    );
  }, []);

  const value = useMemo<ShopState>(
    () => ({
      verification,
      setVerification,
      selection,
      setSelection: (next) => setSelectionState((prev) => ({ ...prev, ...next })),
      cart,
      addToCart,
      removeFromCart,
      cartTotalQty,
      cartTotalGrosze,
      checkout,
      setCheckout: (next) => setCheckoutState((prev) => ({ ...prev, ...next })),
      lastOrder,
      setLastOrder,
      resetOrderSession,
      catalog,
      refreshCatalog,
      markShopClosed,
    }),
    [
      verification,
      setVerification,
      selection,
      cart,
      addToCart,
      removeFromCart,
      cartTotalQty,
      cartTotalGrosze,
      checkout,
      lastOrder,
      resetOrderSession,
      catalog,
      markShopClosed,
    ],
  );

  return <ShopCtx.Provider value={value}>{children}</ShopCtx.Provider>;
}

export function useShop() {
  const ctx = useContext(ShopCtx);
  if (!ctx) throw new Error('useShop must be used inside ShopProvider');
  return ctx;
}

export function findProductForColor(products: Product[], color: ShirtColor): Product | undefined {
  return products.find((p) => p.color === color);
}

export function findSelectedSize(
  product: Product,
  selection: ShopSelection,
): ProductSize | undefined {
  return product.sizes.find((s) => s.cut === selection.cut && s.size === selection.size);
}

export function availableSizesForCut(product: Product, cut: ShirtCut): Size[] {
  return product.sizes.filter((s) => s.cut === cut).map((s) => s.size);
}

export type ColorRoom = ColorAllowance & {
  /** Shirts of this colour the cart can still take. */
  left: number;
  /** Shirts of any colour the cart can still take. */
  leftOverall: number;
};

/**
 * How much of `color` still fits once the cart is counted. The colour's share and the external
 * order's total both bind.
 */
export function roomForColor(
  verification: Verification,
  cart: CartItem[],
  color: ShirtColor,
): ColorRoom {
  const share = verification.byColor[color];
  const inCart = cart.reduce((s, r) => s + r.qty, 0);
  const inCartOfColor = cart.filter((r) => r.color === color).reduce((s, r) => s + r.qty, 0);
  const leftOverall = verification.remaining - inCart;
  return {
    ...share,
    left: Math.min(share.remaining - inCartOfColor, leftOverall),
    leftOverall,
  };
}

function newCartItemId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `ci_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}
